import { Close, CloudUpload, InsertDriveFile } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import Dropzone from "../Dropzone/Dropzone";

type UploadFileComponentProps<FileType> = {
  handleDrop?: (e: React.DragEvent<Element>) => void;
  files: FileType[];
  handleFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  multiple?: boolean;
  text?: string;
  uploadText?: string;
  onRemove?: (file: FileType, index: number) => void;
  disabled?: boolean;
  size?: "small" | "large";
  renderer: (file: FileType) => JSX.Element | undefined;
  onDownload?: (file: FileType) => void;
};

const MAX_NAME_LENGTH_SMALL = 10;
const MAX_NAME_LENGTH_LARGE = 15;
const SIZE_SMALL = 100;
const SIZE_LARGE = 150;

const UploadFileComponent = <FileType,>({
  handleDrop,
  files,
  handleFileChange,
  multiple,
  accept,
  text,
  uploadText,
  onRemove,
  disabled,
  size,
  renderer,
  onDownload,
}: UploadFileComponentProps<FileType>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setHovered(true);
  };

  const getExtFromString = (name: string): string => {
    const parts = name.split(".");

    if (!parts) return "";
    if (parts && parts.length < 1) return "";

    return parts.length > 1 ? parts.pop() || "" : "";
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isImageFile = (file: any): boolean => {
    if (!file) return false;

    const imageExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "webp",
      "bmp",
      "avif",
      "pdf",
      "svg",
    ];
    const extension = file.contentType?.split("/").pop().toLowerCase();
    if (!extension) return false;

    return !!imageExtensions.find((ext) => ext === extension);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getFileName = (file: any): string => {
    const actualName = file.name as string;

    // If file name does not exist, extract from URL.
    const decodedUrl = decodeURIComponent(file.key || "");
    return actualName && actualName != ""
      ? actualName
      : decodedUrl.split("/").pop() || "Unnamed File";
  };

  const clampString = (text: string): string => {
    const maxLength =
      size === "large" ? MAX_NAME_LENGTH_LARGE : MAX_NAME_LENGTH_SMALL;
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setHovered(false);
    }, 50);
  };

  const holderStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: size === "large" ? SIZE_LARGE : SIZE_SMALL,
    width: size === "large" ? SIZE_LARGE : SIZE_SMALL,
    bgcolor: "rgba(0, 0, 0, 0.1)",
    color: "text.secondary",
    overflow: "hidden",
    borderRadius: "8px",
    borderBottom: "3px solid rgba(0, 0, 0, 0.12)",
    position: "relative",
  };

  const renderControls = (file: FileType) => {
    if (!disabled)
      return (
        <>
          <IconButton
            className="remove-button"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.(file, files.indexOf(file));
            }}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              opacity: 0,
              transition: "all 0.2s ease",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 2,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </>
      );
  };

  const renderContent = (file: FileType) => {
    const renderedContent = renderer(file);
    if (isImageFile(file)) {
      if (renderedContent)
        return (
          <Box
            sx={{
              ...holderStyle,
              width: "auto",
              minWidth: size === "large" ? SIZE_LARGE : SIZE_SMALL,
              maxHeight: size === "large" ? SIZE_LARGE : SIZE_SMALL,
              position: "relative",
              "& img": {
                height: "100%",
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {renderedContent}
              {renderControls(file)}
            </Box>
          </Box>
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fileName = getFileName(file);

    return (
      <Box
        sx={{
          ...holderStyle,
          width: size === "large" ? SIZE_LARGE : SIZE_SMALL,
          cursor: "pointer",
        }}
        onClick={() => onDownload?.(file)}
      >
        {renderControls(file)}
        <InsertDriveFile sx={{ fontSize: size === "large" ? 48 : 32 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mt: 1 }}
        >
          {clampString(fileName)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          {getExtFromString(fileName).toUpperCase() + " File" ||
            "No Preview Available"}
        </Typography>
      </Box>
    );
  };

  return (
    <Dropzone
      variant={disabled ? "subtle" : "obvious"}
      onDrop={(e) => {
        if (disabled || (files.length > 0 && !multiple)) return false;

        setHovered(false);
        handleDrop?.(e);
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      sx={{
        position: "relative",
        py: files.length > 0 ? 2 : 3,
        px: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        border: "1px dashed rgba(0, 0, 0, 0.12)",
        borderRadius: "8px",
        backgroundColor: !hovered
          ? "rgba(0, 0, 0, 0.02)"
          : "rgba(0, 0, 0, 0.08)",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.03)",
          borderColor: "rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      {!files.length && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            pb: 2,
          }}
        >
          <CloudUpload
            sx={{
              fontSize: 40,
              color: "text.secondary",
              opacity: 0.5,
            }}
          />
          {text && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              {text}
            </Typography>
          )}
        </Box>
      )}

      {/* Files grid */}
      {files.length > 0 && (
        <Grid
          container
          alignContent={"center"}
          justifyContent={"center"}
          sx={{
            gap: 1,
            width: "100%",
            margin: 0,
            padding: 0.5,
          }}
        >
          {files.filter(Boolean).map((file) => (
            <Grid
              item
              key={uuid()}
              sx={{
                position: "relative",
                borderRadius: 1,
                "&:hover .remove-button": {
                  opacity: 1,
                },
              }}
            >
              {renderContent(file)}
            </Grid>
          ))}
        </Grid>
      )}

      {!(disabled || (files.length > 0 && !multiple)) &&
        (files.length < 1 ? (
          <Button
            variant="outlined"
            size="small"
            onClick={() => inputRef.current?.click()}
            startIcon={<CloudUpload />}
            sx={{
              position: "relative",
              textTransform: "none",
              minHeight: 32,
              padding: "4px 12px",
              fontSize: "0.875rem",
              opacity: files.length ? 0.7 : 1,
              "&:hover": {
                opacity: 1,
              },
            }}
          >
            {files.length ? "Add more" : uploadText || "Click to upload"}
          </Button>
        ) : (
          <Tooltip title="Upload more files">
            <IconButton
              onClick={() => inputRef.current?.click()}
              sx={{
                position: "absolute",
                right: 15,
                top: 15,
                backgroundColor: "rgba(255, 255, 255, 1)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                textTransform: "none",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                },
              }}
            >
              <CloudUpload
                sx={{
                  opacity: 0.5,
                }}
              />
            </IconButton>
          </Tooltip>
        ))}

      <input
        ref={inputRef}
        accept={accept ?? "image/*"}
        multiple={multiple}
        hidden
        type="file"
        onChange={handleFileChange}
      />
    </Dropzone>
  );
};

export default UploadFileComponent;
